import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type * as React from 'react';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import Sparkline from './Sparkline';

export type HardMetricCardProps = {
  type: string;
  data?: number | null;
  secondaryData?: number;
  tooltip: string;
  percentage?: boolean;
  secondaryDataPercentage?: boolean;
  onActionButtonClick?: () => void;
  navigationTooltip?: string;
  /** Optional sparkline series rendered below the value. */
  sparkline?: number[];
  /** When true, render the sparkline inline (right of the number) instead of below it. */
  inlineSparkline?: boolean;
};

const HardMetricCard: React.FC<HardMetricCardProps> = ({
  type,
  tooltip,
  data = undefined,
  secondaryData = undefined,
  percentage = false,
  secondaryDataPercentage = false,
  onActionButtonClick = undefined,
  navigationTooltip = undefined,
  sparkline,
  inlineSparkline = false,
}) => {
  let displayData;
  if (data === null || data === undefined || data < 0) {
    displayData = '—';
  } else if (data % 1 !== 0 && !percentage) {
    displayData = data.toFixed(2);
  } else if (percentage) {
    displayData = `${(data * 100).toFixed(1)}%`;
  } else {
    displayData = typeof data === 'number' ? data.toLocaleString() : data;
  }

  let displaySecondaryData;
  if (secondaryData || secondaryData === 0) {
    if (secondaryDataPercentage) {
      displaySecondaryData = `${(secondaryData * 100).toFixed(1)}%`;
    } else {
      displaySecondaryData = secondaryData.toLocaleString();
    }
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Tooltip arrow enterDelay={1000} enterNextDelay={1000} leaveDelay={0} title={tooltip} placement="top">
        <span>
          <Card
            elevation={0}
            sx={{
              width: '100%',
              minHeight: 124,
              height: '100%',
              borderRadius: RADIUS_COIN,
              overflow: 'visible',
              boxShadow: SHADOW,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
            }}
          >
            <CardContent
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: inlineSparkline ? 'center' : 'space-between',
                gap: inlineSparkline ? 0.5 : 0,
                p: 2.25,
                '&:last-child': { pb: 2.25 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: `${NAVY}99`,
                    wordBreak: 'break-word',
                    lineHeight: 1.3,
                  }}
                >
                  {type}
                </Typography>
                {onActionButtonClick && (
                  <Tooltip title={navigationTooltip || 'Ver leads'} arrow>
                    <IconButton
                      size="small"
                      onClick={onActionButtonClick}
                      aria-label={navigationTooltip || `Ver leads de ${type}`}
                      sx={{
                        width: 24,
                        height: 24,
                        color: `${NAVY}80`,
                        backgroundColor: 'transparent',
                        transition: 'color 0.15s ease, background-color 0.15s ease',
                        '&:hover': { color: NAVY, backgroundColor: `${NAVY}0d` },
                      }}
                    >
                      <ManageSearchIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: inlineSparkline ? 'center' : 'baseline',
                  flexWrap: 'wrap',
                  columnGap: inlineSparkline ? 1.5 : 1,
                  rowGap: 0.25,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    color: NAVY,
                    fontSize: '2rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {displayData}
                </Typography>
                {(secondaryData || secondaryData === 0) && (
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      color: `${NAVY}80`,
                    }}
                  >
                    ({displaySecondaryData})
                  </Typography>
                )}
                {inlineSparkline && sparkline && sparkline.length > 1 && (
                  <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end', minWidth: 120 }}>
                    <Sparkline values={sparkline} width={220} height={44} />
                  </Box>
                )}
              </Box>

              {!inlineSparkline && sparkline && sparkline.length > 1 && (
                <Box sx={{ mt: 1 }}>
                  <Sparkline values={sparkline} width={120} height={28} />
                </Box>
              )}
            </CardContent>
          </Card>
        </span>
      </Tooltip>
    </Box>
  );
};

export default HardMetricCard;
